import React, { useEffect, useState } from "react";
import { post } from "../api";
import { errText, getLang, t } from "../i18n";
import { navigate, refreshMe } from "../store";
import { Button, Field, Link } from "../ui";
import { PublicFooter, PublicHeader } from "./landing";

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <PublicHeader />
      <div className="auth-card">
        <h1>{title}</h1>
        {children}
      </div>
      <PublicFooter />
    </div>
  );
}

const fieldErrors = (e: any): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const i of e?.extra?.issues ?? []) out[i.path] = i.message;
  return out;
};

export function Login({ next }: { next?: string }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await post("/api/auth/login", { login, password });
      await refreshMe();
      const n = next ?? new URLSearchParams(location.search).get("next");
      navigate(n && n.startsWith("/") && !n.startsWith("//") ? n : "/", true);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t.signIn}>
      <form onSubmit={submit} className="stack">
        <Field label={t.emailOrUsername}>
          <input value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="username" required dir="ltr" />
        </Field>
        <Field label={t.password}>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required dir="ltr" />
        </Field>
        {err && <p className="form-error" role="alert">{err}</p>}
        <Button type="submit" busy={busy}>{t.signIn}</Button>
        <div className="auth-links">
          <Link to="/forgot">{t.forgot}</Link>
          <span>{t.noAccount} <Link to="/register">{t.signUp}</Link></span>
        </div>
      </form>
    </Card>
  );
}

export function Register() {
  const [f, setF] = useState({ email: "", username: "", displayName: "", password: "", acceptTerms: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState<Record<string, string>>({});
  const set = (k: string, v: any) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setFe({});
    try {
      await post("/api/auth/register", { ...f, displayName: f.displayName || undefined, locale: getLang() });
      await refreshMe();
      navigate("/", true);
    } catch (e: any) {
      setErr(errText(e));
      setFe(fieldErrors(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t.signUp}>
      <form onSubmit={submit} className="stack">
        <Field label={t.email} error={fe.email}>
          <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" required dir="ltr" />
        </Field>
        <Field label={t.username} hint={t.usernameHint} error={fe.username}>
          <input value={f.username} onChange={(e) => set("username", e.target.value)} autoComplete="username" required pattern="[A-Za-z0-9_.]{3,20}" dir="ltr" />
        </Field>
        <Field label={t.displayName} error={fe.displayName}>
          <input value={f.displayName} onChange={(e) => set("displayName", e.target.value)} maxLength={40} />
        </Field>
        <Field label={t.password} hint={t.passwordHint} error={fe.password}>
          <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" minLength={8} required dir="ltr" />
        </Field>
        <label className="check">
          <input type="checkbox" checked={f.acceptTerms} onChange={(e) => set("acceptTerms", e.target.checked)} required />
          <span>
            <Link to="/terms">{t.terms}</Link> · <Link to="/privacy">{t.privacy}</Link> — {t.acceptTerms}
          </span>
        </label>
        {err && <p className="form-error" role="alert">{err}</p>}
        <Button type="submit" busy={busy}>{t.signUp}</Button>
        <div className="auth-links">
          <span>{t.haveAccount} <Link to="/login">{t.signIn}</Link></span>
        </div>
      </form>
    </Card>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  return (
    <Card title={t.forgot}>
      {done ? (
        <p className="notice">{t.resetSent}</p>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await post("/api/auth/forgot", { email });
              setDone(true);
            } catch (e) {
              setErr(errText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" />
          </Field>
          {err && <p className="form-error">{err}</p>}
          <Button type="submit" busy={busy}>{t.sendResetLink}</Button>
        </form>
      )}
    </Card>
  );
}

export function Reset() {
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Card title={t.newPassword}>
      {done ? (
        <div className="stack">
          <p className="notice">{t.passwordChanged}</p>
          <Link to="/login" className="btn btn-primary">{t.signIn}</Link>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await post("/api/auth/reset", { token, password });
              setDone(true);
            } catch (e) {
              setErr(errText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t.newPassword} hint={t.passwordHint}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="new-password" dir="ltr" />
          </Field>
          {err && <p className="form-error">{err}</p>}
          <Button type="submit" busy={busy}>{t.setPassword}</Button>
        </form>
      )}
    </Card>
  );
}

export function Verify() {
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [msg, setMsg] = useState(t.loading);
  useEffect(() => {
    post("/api/auth/verify-email", { token }).then(
      () => {
        setMsg(t.emailVerified);
        void refreshMe();
      },
      (e) => setMsg(errText(e)),
    );
  }, [token]);
  return (
    <Card title={t.email}>
      <p className="notice">{msg}</p>
      <Link to="/" className="btn btn-primary">{t.home}</Link>
    </Card>
  );
}
